import React, { useState, useEffect } from "react";
import Layout from "@/components/Layout";

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { toast } from "sonner";
import { api, getApiErrorMessage } from "@/lib/api";
import { TANK_SENSOR_TYPE_OPTIONS } from "@/lib/tankSensorTypes";
import {
  GSM_APN_OPTIONS,
  LEVEL_CONNECTION_TYPE_OPTIONS,
} from "@/lib/networkConnectionTypes";

import {
  Building2,
  Cpu,
  Droplets,
  Power,
  Network,
  GitBranch,
  X,
  Plus,
  ChevronRight,
  Layers,
  Waves,
  Zap,
  FlaskConical,
  Activity,
  Edit2,
  Trash2,
  Users,
} from "lucide-react";

const MAX_ROWS = 20;

/* ─────────────────────────────────────────────
   Beautiful Flow Visualization with Colorful Blocks
───────────────────────────────────────────── */
const FlowTree = ({
  flow,
  societiesData,
  adminsData,
  ugTanksData,
  towersData,
  pumpControllersData,
  pumpsData,
  levelControllersData,
  tanksData,
  valvesData,
}) => {
  const label = (list, idField, id, displayField = "name") => {
    if (!list || !list.length) return id;
    const found = list.find((x) => x[idField] === id);
    return found ? found[displayField] : id;
  };

  const assignedUserIds =
    Array.isArray(flow.user_ids) && flow.user_ids.length
      ? flow.user_ids
      : flow.user_id
        ? [flow.user_id]
        : [];
  const assignedUsersLabel = assignedUserIds
    .map((id) => {
      const admin = adminsData.find((item) => item.id === id);
      return admin?.name || admin?.email || id;
    })
    .filter(Boolean)
    .join(", ");

  const FlowBlock = ({ icon: Icon, title, value, bgColor, isSmall }) => (
    <div
      className={`${bgColor} rounded-lg shadow-md hover:shadow-lg transition-all ${isSmall ? "px-2.5 py-1.5" : "px-3 py-2"}`}
    >
      <div
        className={`flex items-center gap-1.5 text-white ${isSmall ? "min-w-[75px]" : "min-w-[110px]"}`}
      >
        <Icon className={`flex-shrink-0 ${isSmall ? "h-3 w-3" : "h-4 w-4"}`} />
        <div className="min-w-0">
          <div
            className={`font-semibold opacity-90 leading-tight ${isSmall ? "text-[9px]" : "text-xs"}`}
          >
            {title}
          </div>
          <div
            className={`font-bold truncate ${isSmall ? "text-[8px]" : "text-xs"}`}
          >
            {value}
          </div>
        </div>
      </div>
    </div>
  );

  const Arrow = ({ isSmall }) => (
    <div
      className={`flex-shrink-0 text-slate-400 ${isSmall ? "px-1" : "px-2"}`}
    >
      <ChevronRight className={isSmall ? "h-4 w-4" : "h-5 w-5"} />
    </div>
  );

  const mappings = (flow.valve_tank_mappings || []).filter(
    (m) => m?.tank_id || m?.valve_id || m?.pump_id,
  );

  const mappingLabel = (mapping, field, list, idField, displayField) => {
    const displayValue = mapping[`${field.replace("_id", "")}_display`];
    return displayValue || label(list, idField, mapping[field], displayField);
  };

  const MappingRow = ({ mapping }) => {
    const blocks = [
      mapping.tank_id && {
        key: "tank",
        icon: Waves,
        title: "Tank",
        value: mappingLabel(mapping, "tank_id", tanksData, "id", "tank_id"),
        bgColor: "bg-gradient-to-r from-teal-500 to-teal-600",
      },
      mapping.valve_id && {
        key: "valve",
        icon: FlaskConical,
        title: "Valve",
        value: mappingLabel(mapping, "valve_id", valvesData, "id", "valve_id"),
        bgColor: "bg-gradient-to-r from-pink-500 to-pink-600",
      },
      mapping.pump_id && {
        key: "pump",
        icon: Activity,
        title: "Pump",
        value: mappingLabel(mapping, "pump_id", pumpsData, "id", "pump_id"),
        bgColor: "bg-gradient-to-br from-orange-500 to-orange-600",
      },
    ].filter(Boolean);

    return (
      <div className="flex items-center gap-0">
        {blocks.map((block, index) => (
          <React.Fragment key={block.key}>
            <FlowBlock {...block} isSmall={true} />
            {index < blocks.length - 1 && <Arrow isSmall={true} />}
          </React.Fragment>
        ))}
      </div>
    );
  };

  const SingleMapping = ({ mapping }) => {
    const blocks = [
      mapping.tank_id && {
        key: "tank",
        icon: Waves,
        title: "Tank",
        value: mappingLabel(mapping, "tank_id", tanksData, "id", "tank_id"),
        bgColor: "bg-gradient-to-r from-teal-500 to-teal-600",
      },
      mapping.valve_id && {
        key: "valve",
        icon: FlaskConical,
        title: "Valve",
        value: mappingLabel(mapping, "valve_id", valvesData, "id", "valve_id"),
        bgColor: "bg-gradient-to-r from-pink-500 to-pink-600",
      },
      mapping.pump_id && {
        key: "pump",
        icon: Activity,
        title: "Pump",
        value: mappingLabel(mapping, "pump_id", pumpsData, "id", "pump_id"),
        bgColor: "bg-gradient-to-br from-orange-500 to-orange-600",
      },
    ].filter(Boolean);

    return (
      <>
        {blocks.map((block, index) => (
          <React.Fragment key={block.key}>
            <FlowBlock {...block} isSmall={true} />
            {index < blocks.length - 1 && <Arrow isSmall={true} />}
          </React.Fragment>
        ))}
      </>
    );
  };

  return (
    <div className="space-y-2">
      {/* Main Flow */}
      <div className="overflow-x-auto pb-2 -mx-2 px-2">
        <div className="flex items-start gap-0 min-w-max">
          <div className="flex items-center gap-0 pt-2">
            {/* Society */}
            <FlowBlock
              icon={Building2}
              title="Society"
              value={label(
                societiesData ? [societiesData] : [],
                "id",
                flow.society_id,
              )}
              bgColor="bg-gradient-to-br from-blue-500 to-blue-600"
            />
            <Arrow />

            {/* UG Tank - Only if selected */}
            {flow.ug_tank_id && (
              <>
                <FlowBlock
                  icon={Droplets}
                  title="UG Tank"
                  value={label(
                    ugTanksData,
                    "id",
                    flow.ug_tank_id,
                    "ug_tank_id",
                  )}
                  bgColor="bg-gradient-to-br from-cyan-500 to-cyan-600"
                />
                <Arrow />
              </>
            )}

            {/* Pump Controller - Only if selected */}
            {flow.pump_controller_id && (
              <>
                <FlowBlock
                  icon={Zap}
                  title="Pump Ctrl"
                  value={label(
                    pumpControllersData,
                    "id",
                    flow.pump_controller_id,
                    "device_id",
                  )}
                  bgColor="bg-gradient-to-br from-yellow-500 to-yellow-600"
                />
                <Arrow />
              </>
            )}

            {/* Tower */}
            <FlowBlock
              icon={Layers}
              title="Tower"
              value={label(towersData, "id", flow.tower_id)}
              bgColor="bg-gradient-to-br from-purple-500 to-purple-600"
            />

            {assignedUsersLabel && (
              <>
                <Arrow />
                <FlowBlock
                  icon={Users}
                  title="Admins"
                  value={assignedUsersLabel}
                  bgColor="bg-gradient-to-br from-slate-600 to-slate-700"
                />
              </>
            )}

            {/* Level Controller - Only if selected */}
            {flow.level_controller_id && (
              <>
                <Arrow />
                <FlowBlock
                  icon={Network}
                  title="Level Ctrl"
                  value={label(
                    levelControllersData,
                    "id",
                    flow.level_controller_id,
                    "device_id",
                  )}
                  bgColor="bg-gradient-to-br from-indigo-500 to-indigo-600"
                />
              </>
            )}
          </div>

          {mappings.length === 1 && (
            <>
              <div className="pt-7">
                <Arrow />
              </div>
              <div className="flex items-center gap-0 pt-4">
                <SingleMapping mapping={mappings[0]} />
              </div>
            </>
          )}

          {mappings.length > 1 && (
            <>
              <div className="pt-7">
                <Arrow />
              </div>
              <div className="relative flex flex-col gap-3 border-l-2 border-slate-300 pl-4">
                {mappings.map((mapping, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <div className="h-px w-5 bg-slate-300" />
                    <MappingRow mapping={mapping} />
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────
   Live preview tree while filling the form
───────────────────────────────────────────── */
const LivePreviewTree = ({
  flow,
  currentVTMapping,
  societiesData,
  adminsData,
  ugTanksData,
  towersData,
  pumpControllersData,
  pumpsData,
  levelControllersData,
  tanksData,
  valvesData,
}) => {
  const label = (list, idField, id, displayField = "name") => {
    if (!list || !id) return null;
    const arr = Array.isArray(list) ? list : [list];
    const found = arr.find((x) => x[idField] === id);
    return found ? found[displayField] : id;
  };

  const societyLabel =
    societiesData && flow.society_id
      ? societiesData.id === flow.society_id
        ? societiesData.name
        : flow.society_id
      : null;
  const assignedUserIds =
    Array.isArray(flow.user_ids) && flow.user_ids.length
      ? flow.user_ids
      : flow.user_id
        ? [flow.user_id]
        : [];
  const assignedUsersLabel = assignedUserIds
    .map((id) => {
      const admin = adminsData.find((item) => item.id === id);
      return admin?.name || admin?.email || id;
    })
    .filter(Boolean)
    .join(", ");

  return (
    <div className="min-h-[220px] overflow-y-auto rounded-xl border border-slate-200 bg-white p-4 shadow-sm lg:h-full lg:min-h-0">
      <p className="text-xs font-semibold text-slate-400 uppercase tracking-widest mb-3">
        Live Preview
      </p>
      {!flow.society_id ? (
        <div className="flex min-h-40 items-center justify-center rounded-lg border border-dashed border-slate-200 bg-slate-50 px-4 text-center text-sm text-slate-500">
          Flow preview will appear as you configure the mapping.
        </div>
      ) : (
      <div className="font-mono text-sm">
        {/* Society */}
        <div className="flex items-center gap-1 text-slate-700 font-semibold">
          <Building2 className="h-4 w-4 text-blue-500" />
          <span>{societyLabel || flow.society_id}</span>
        </div>

        {/* UG Tank */}
        {flow.ug_tank_id && (
          <div className="ml-4 border-l-2 border-cyan-200 pl-3 mt-1">
            <div className="flex items-center gap-1 text-cyan-700">
              <Droplets className="h-3 w-3" />
              <span>
                {label(ugTanksData, "id", flow.ug_tank_id, "ug_tank_id")}
              </span>
            </div>
          </div>
        )}

        {/* Pump Controller Branch */}
        {flow.pump_controller_id && (
          <div className="ml-4 border-l-2 border-yellow-200 pl-3 mt-1">
            <div className="flex items-center gap-1 text-yellow-700">
              <Zap className="h-3 w-3" />
              <span>
                {label(
                  pumpControllersData,
                  "id",
                  flow.pump_controller_id,
                  "device_id",
                )}
              </span>
            </div>
          </div>
        )}

        {/* Tower (Independent) */}
        {flow.tower_id && (
          <div className="ml-4 border-l-2 border-purple-200 pl-3 mt-1">
            <div className="flex items-center gap-1 text-purple-700">
              <Layers className="h-3 w-3" />
              <span>{label(towersData, "id", flow.tower_id)}</span>
            </div>
          </div>
        )}

        {assignedUsersLabel && (
          <div className="ml-4 border-l-2 border-slate-200 pl-3 mt-1">
            <div className="flex items-center gap-1 text-slate-700">
              <Users className="h-3 w-3" />
              <span>{assignedUsersLabel}</span>
            </div>
          </div>
        )}

        {/* Level Controller Branch (Independent) */}
        {flow.level_controller_id && (
          <div className="ml-4 border-l-2 border-indigo-200 pl-3 mt-1">
            <div className="flex items-center gap-1 text-indigo-700">
              <Network className="h-3 w-3" />
              <span>
                {label(
                  levelControllersData,
                  "id",
                  flow.level_controller_id,
                  "device_id",
                )}
              </span>
            </div>

            {/* Valve-Tank Mappings (inside level controller) */}
            {flow.valve_tank_mappings.map((m, i) => (
              <div key={i} className="ml-4 border-l-2 border-pink-200 pl-3 mt-1">
                <div className="flex items-center gap-1 flex-wrap">
                  {m.pump_id && (
                    <>
                      <span className="text-orange-700 flex items-center gap-1">
                        <Activity className="h-3 w-3" />
                        {label(pumpsData, "id", m.pump_id, "pump_id")}
                      </span>
                      <ChevronRight className="h-3 w-3 text-slate-300" />
                    </>
                  )}
                  {m.valve_id && (
                    <>
                      <span className="text-pink-700 flex items-center gap-1">
                        <FlaskConical className="h-3 w-3" />
                        {label(valvesData, "id", m.valve_id, "valve_id")}
                      </span>
                      {m.tank_id && (
                        <ChevronRight className="h-3 w-3 text-slate-300" />
                      )}
                    </>
                  )}
                  {m.tank_id && (
                    <span className="text-teal-700 flex items-center gap-1">
                      <Waves className="h-3 w-3" />
                      {label(tanksData, "id", m.tank_id, "tank_id")}
                    </span>
                  )}
                </div>
              </div>
            ))}

            {/* In-progress mapping */}
            {(currentVTMapping.valve_id ||
              currentVTMapping.tank_id ||
              currentVTMapping.pump_id) && (
              <div className="ml-4 border-l-2 border-dashed border-pink-100 pl-3 mt-1 flex items-center gap-1 text-pink-400 italic flex-wrap">
                {currentVTMapping.pump_id && (
                  <>
                    <span className="flex items-center gap-1">
                      <Activity className="h-3 w-3" />
                      {label(
                        pumpsData,
                        "id",
                        currentVTMapping.pump_id,
                        "pump_id",
                      )}
                    </span>
                    {(currentVTMapping.valve_id || currentVTMapping.tank_id) && (
                      <ChevronRight className="h-3 w-3 text-slate-300" />
                    )}
                  </>
                )}
                {currentVTMapping.valve_id && (
                  <>
                    <span className="flex items-center gap-1">
                      <FlaskConical className="h-3 w-3" />
                      {label(
                        valvesData,
                        "id",
                        currentVTMapping.valve_id,
                        "valve_id",
                      )}
                    </span>
                    {currentVTMapping.tank_id && (
                      <ChevronRight className="h-3 w-3 text-slate-300" />
                    )}
                  </>
                )}
                {currentVTMapping.tank_id && (
                  <span className="text-teal-400 flex items-center gap-1">
                    <Waves className="h-3 w-3" />
                    {label(tanksData, "id", currentVTMapping.tank_id, "tank_id")}
                  </span>
                )}
              </div>
            )}
          </div>
        )}
      </div>
      )}
    </div>
  );
};

/* ══════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════ */
const DeviceRegistration = () => {
  const [loading, setLoading] = useState(false);

  const generateRows = (count, template) => {
    const num = Number(count);
    if (!num || num <= 0) {
      toast.error("Please enter a valid count");
      return [];
    }
    if (num > MAX_ROWS) {
      toast.error(`Maximum ${MAX_ROWS} items allowed`);
      return [];
    }
    return Array.from({ length: num }, () => ({ ...template }));
  };

  /* ─── SOCIETY ─── */
  const [society, setSociety] = useState({ name: "", location: "" });
  const [editingSociety, setEditingSociety] = useState(false);

  const submitSociety = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (societiesData?.id) {
        await api.updateSociety(society);
        toast.success("Society updated successfully");
        setEditingSociety(false);
      } else {
        await api.createSociety(society);
        toast.success("Society created successfully");
      }
      setSociety({ name: "", location: "" });
      await refetchDropdownData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Failed to save society"));
    } finally {
      setLoading(false);
    }
  };

  const startEditSociety = () => {
    if (!societiesData?.id) return;
    setSociety({
      name: societiesData.name || "",
      location: societiesData.location || "",
    });
    setEditingSociety(true);
  };

  const cancelEditSociety = () => {
    setSociety({ name: "", location: "" });
    setEditingSociety(false);
  };

  const deleteSociety = async () => {
    if (!societiesData?.id) return;
    if (
      !window.confirm(
        "Delete this society? This is only allowed when no related records exist.",
      )
    ) {
      return;
    }

    setLoading(true);
    try {
      await api.deleteSociety();
      toast.success("Society deleted successfully");
      setSociety({ name: "", location: "" });
      setEditingSociety(false);
      await refetchDropdownData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Failed to delete society"));
    } finally {
      setLoading(false);
    }
  };

  /* ─── INFRASTRUCTURE ─── */
  const [ugCount, setUgCount] = useState("");
  const [ugTanks, setUgTanks] = useState([]);
  const [towerCount, setTowerCount] = useState("");
  const [towers, setTowers] = useState([]);
  const [tankCount, setTankCount] = useState("");
  const [tanks, setTanks] = useState([]);

  /* ─── DEVICES ─── */
  const [levelCount, setLevelCount] = useState("");
  const [levelDevices, setLevelDevices] = useState([]);
  const [pumpControllerCount, setPumpControllerCount] = useState("");
  const [pumpControllers, setPumpControllers] = useState([]);
  const [pumpCount, setPumpCount] = useState("");
  const [pumps, setPumps] = useState([]);
  const [valveCount, setValveCount] = useState("");
  const [valves, setValves] = useState([]);

  /* ─── DROPDOWN DATA ─── */
  const [societiesData, setSocietiesData] = useState([]);
  const [adminsData, setAdminsData] = useState([]);
  const [ugTanksData, setUgTanksData] = useState([]);
  const [towersData, setTowersData] = useState([]);
  const [pumpControllersData, setPumpControllersData] = useState([]);
  const [pumpsData, setPumpsData] = useState([]);
  const [levelControllersData, setLevelControllersData] = useState([]);
  const [tanksData, setTanksData] = useState([]);
  const [valvesData, setValvesData] = useState([]);

  /* ─── REFETCH DROPDOWN DATA ─── */
  const hasValue = (value) =>
    value !== null && value !== undefined && String(value).trim() !== "";

  const validateRows = (rows, rules, label) => {
    if (!rows.length) {
      toast.error(`Please generate at least one ${label}`);
      return false;
    }

    for (let index = 0; index < rows.length; index += 1) {
      const row = rows[index];

      for (const rule of rules) {
        const value = rule.getValue ? rule.getValue(row) : row[rule.key];

        if (!hasValue(value)) {
          toast.error(`${label} row ${index + 1}: ${rule.label} is required`);
          return false;
        }

        if (rule.type === "number") {
          const numericValue = Number(value);

          if (!Number.isFinite(numericValue)) {
            toast.error(`${label} row ${index + 1}: ${rule.label} must be a number`);
            return false;
          }

          if (rule.min !== undefined && numericValue < rule.min) {
            toast.error(`${label} row ${index + 1}: ${rule.label} must be at least ${rule.min}`);
            return false;
          }

          if (rule.max !== undefined && numericValue > rule.max) {
            toast.error(`${label} row ${index + 1}: ${rule.label} must be at most ${rule.max}`);
            return false;
          }
        }

        if (rule.allowedValues) {
          const allowedValues = rule.allowedValues.map(String);

          if (!allowedValues.includes(String(value))) {
            toast.error(
              `${label} row ${index + 1}: ${rule.label} must be one of ${rule.allowedMessage || allowedValues.join(", ")}`,
            );
            return false;
          }
        }
      }
    }

    return true;
  };

  const refetchDropdownData = async () => {
    try {
      const [
        societies,
        admins,
        ugTanks,
        towers,
        pumpControllers,
        pumps,
        levelControllers,
        tanks,
        valves,
      ] = await Promise.all([
        api
          .getSocieties()
          .then((r) => ({ data: r.data }))
          .catch((err) => {
            toast.error(getApiErrorMessage(err, "Failed to load societies"));
            return { data: null };
          }),
        api
          .getAdminUsers()
          .then((r) => ({ data: r }))
          .catch(() => ({ data: [] })),
        api
          .getUGTanks()
          .then((r) => ({ data: r.data }))
          .catch(() => ({ data: [] })),
        api
          .getTowers()
          .then((r) => ({ data: r.data }))
          .catch(() => ({ data: [] })),
        api
          .getPumpControllers()
          .then((r) => ({ data: r.data }))
          .catch(() => ({ data: [] })),
        api
          .getPumps()
          .then((r) => ({ data: r.data }))
          .catch(() => ({ data: [] })),
        api
          .getLevelControllers()
          .then((r) => ({ data: r.data }))
          .catch(() => ({ data: [] })),
        api
          .getTanks()
          .then((r) => ({ data: r.data }))
          .catch(() => ({ data: [] })),
        api
          .getValves()
          .then((r) => ({ data: r.data }))
          .catch(() => ({ data: [] })),
      ]);
      setSocietiesData(societies.data || null);
      setAdminsData(admins.data || []);
      setUgTanksData(ugTanks.data || []);
      setTowersData(towers.data || []);
      setPumpControllersData(pumpControllers.data || []);
      setPumpsData(pumps.data || []);
      setLevelControllersData(levelControllers.data || []);
      setTanksData(tanks.data || []);
      setValvesData(valves.data || []);
    } catch (error) {
      toast.error("Failed to load dropdown data");
    }
  };

  /* Fetch flows from API on mount */
  const fetchFlows = async () => {
    setLoadingFlows(true);
    try {
      const response = await api.getFlows();
      setSavedFlows(response || []);
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Failed to load flows"));
      setSavedFlows([]);
    } finally {
      setLoadingFlows(false);
    }
  };

  useEffect(() => {
    refetchDropdownData();
    fetchFlows();
  }, []);

  /* ─── ARCHITECTURE – FLOWS ─── */
  const [savedFlows, setSavedFlows] = useState([]);
  const [loadingFlows, setLoadingFlows] = useState(false);
  const [showFlowModal, setShowFlowModal] = useState(false);
  const [editingFlowId, setEditingFlowId] = useState(null);

  const getEmptyFlow = () => ({
    society_id: societiesData?.id || "",
    user_id: "",
    user_ids: [],
    ug_tank_id: "",
    pump_controller_id: "",
    tower_id: "",
    level_controller_id: "",
    valve_tank_mappings: [],
  });

  const [currentFlow, setCurrentFlow] = useState(getEmptyFlow);
  const [currentVTMapping, setCurrentVTMapping] = useState({
    valve_id: "",
    tank_id: "",
    pump_id: "",
  });

  useEffect(() => {
    if (!societiesData?.id || editingFlowId) return;

    setCurrentFlow((prev) =>
      prev.society_id ? prev : { ...prev, society_id: societiesData.id },
    );
  }, [societiesData?.id, editingFlowId]);

  /* Helper function to get display labels from UUID */
  const getDisplayLabel = (list, id, displayField = "name") => {
    if (!list || !id) return "";
    const arr = Array.isArray(list) ? list : [list];
    const found = arr.find((x) => x.id === id);
    return found ? found[displayField] : id;
  };

  const getFlowUserIds = (flow) =>
    Array.isArray(flow.user_ids) && flow.user_ids.length
      ? flow.user_ids
      : flow.user_id
        ? [flow.user_id]
        : [];

  const getAdminLabel = (adminId) => {
    const admin = adminsData.find((item) => item.id === adminId);
    return admin?.name || admin?.email || adminId;
  };

  const toggleAssignedUser = (adminId) => {
    setCurrentFlow((prev) => {
      const currentUserIds = getFlowUserIds(prev);
      const user_ids = currentUserIds.includes(adminId)
        ? currentUserIds.filter((id) => id !== adminId)
        : [...currentUserIds, adminId];

      return {
        ...prev,
        user_ids,
        user_id: user_ids[0] || "",
      };
    });
  };

  /* Compute already-used IDs from saved flows (excluding current flow if editing) */
  const usedLevelControllerIds = savedFlows
    .filter((f) => f.id !== editingFlowId)
    .map((f) => f.level_controller_id)
    .filter(Boolean);
  const usedValveIds = savedFlows
    .filter((f) => f.id !== editingFlowId)
    .flatMap((f) => f.valve_tank_mappings.map((m) => m.valve_id))
    .filter(Boolean);
  const usedTankIds = savedFlows
    .filter((f) => f.id !== editingFlowId)
    .flatMap((f) => f.valve_tank_mappings.map((m) => m.tank_id))
    .filter(Boolean);

  /* Also exclude items already selected in the current in-progress flow */
  const currentFlowUsedValveIds = currentFlow.valve_tank_mappings.map(
    (m) => m.valve_id,
  );
  const currentFlowUsedTankIds = currentFlow.valve_tank_mappings.map(
    (m) => m.tank_id,
  );

  const availableTowers = towersData;
  const availableLevelControllers = levelControllersData.filter(
    (lc) => !usedLevelControllerIds.includes(lc.id),
  );
  const availableValves = valvesData.filter(
    (v) =>
      !usedValveIds.includes(v.id) && !currentFlowUsedValveIds.includes(v.id),
  );
  const availableTanks = tanksData.filter(
    (t) =>
      !usedTankIds.includes(t.id) && !currentFlowUsedTankIds.includes(t.id),
  );

  const addVTMapping = () => {
    const { valve_id, tank_id, pump_id } = currentVTMapping;
    if (!valve_id && !tank_id) {
      toast.error("Please select at least a valve or a tank");
      return;
    }
    setCurrentFlow((prev) => ({
      ...prev,
      valve_tank_mappings: [
        ...prev.valve_tank_mappings,
        {
          valve_id: valve_id,
          tank_id: tank_id,
          ...(pump_id && { pump_id: pump_id }),
        },
      ],
    }));
    setCurrentVTMapping({ valve_id: "", tank_id: "", pump_id: "" });
    toast.success("Mapping added");
  };

  const removeVTMapping = (index) => {
    setCurrentFlow((prev) => ({
      ...prev,
      valve_tank_mappings: prev.valve_tank_mappings.filter(
        (_, i) => i !== index,
      ),
    }));
  };

  const saveFlow = async () => {
    const {
      society_id,
      ug_tank_id,
      pump_controller_id,
      tower_id,
      level_controller_id,
      user_id,
      user_ids,
      valve_tank_mappings,
    } = currentFlow;
    const assignedUserIds = user_ids?.length ? user_ids : user_id ? [user_id] : [];
    const configuredSocietyId = society_id || societiesData?.id || "";

    /* Validate required fields */
    if (!configuredSocietyId) {
      toast.error("Society configuration is not available yet");
      return;
    }
    if (!tower_id) {
      toast.error("Please select a tower");
      return;
    }
    if (assignedUserIds.length === 0) {
      toast.error("Please assign at least one user");
      return;
    }

    /* At least one of pump_controller or level_controller is required */
    if (!pump_controller_id && !level_controller_id) {
      toast.error(
        "Please select either a pump controller or a level controller",
      );
      return;
    }

    /* If level_controller is selected, at least one valve or tank mapping is required */
    if (level_controller_id && valve_tank_mappings.length === 0) {
      toast.error(
        "Please add at least one valve or tank mapping for the level controller",
      );
      return;
    }

    setLoading(true);
    try {
      if (editingFlowId) {
        /* Update existing flow - always send all fields to allow clearing via empty strings */
        const updateData = {
          society_id: configuredSocietyId,
          tower_id,
          user_ids: assignedUserIds,
          user_id: assignedUserIds[0] || "",
          ug_tank_id: ug_tank_id || "",
          pump_controller_id: pump_controller_id || "",
          level_controller_id: level_controller_id || "",
          valve_tank_mappings: valve_tank_mappings || [],
        };
        await api.updateFlow(editingFlowId, updateData);
        toast.success("Flow updated successfully");
      } else {
        /* Create new flow - only include fields with values */
        const createData = {
          society_id: configuredSocietyId,
          tower_id,
          user_ids: assignedUserIds,
          user_id: assignedUserIds[0],
          ...(ug_tank_id && { ug_tank_id }),
          ...(pump_controller_id && { pump_controller_id }),
          valve_tank_mappings: valve_tank_mappings || [],
        };
        if (level_controller_id) {
          createData.level_controller_id = level_controller_id;
        }
        // console.log("Creating flow with data:", createData);
        await api.createFlow(createData);
        toast.success("Flow created successfully");
      }

      /* Refresh flows list */
      await fetchFlows();

      /* Reset form */
      setCurrentFlow(getEmptyFlow());
      setCurrentVTMapping({ valve_id: "", tank_id: "", pump_id: "" });
      setEditingFlowId(null);
      setShowFlowModal(false);
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Failed to save flow"));
    } finally {
      setLoading(false);
    }
  };

  const openEditFlow = (flow) => {
    setEditingFlowId(flow.id);
    const user_ids = getFlowUserIds(flow);
    setCurrentFlow({
      ...flow,
      society_id: flow.society_id || societiesData?.id || "",
      user_ids,
      user_id: user_ids[0] || "",
    });
    setCurrentVTMapping({ valve_id: "", tank_id: "", pump_id: "" });
    setShowFlowModal(true);
  };

  const openNewFlow = () => {
    setEditingFlowId(null);
    setCurrentFlow(getEmptyFlow());
    setCurrentVTMapping({ valve_id: "", tank_id: "", pump_id: "" });
    setShowFlowModal(true);
  };

  const deleteFlow = async (id) => {
    if (
      window.confirm(
        "Are you sure you want to delete this flow? This action cannot be undone.",
      )
    ) {
      setLoading(true);
      try {
        await api.deleteFlow(id);
        toast.success("Flow deleted successfully");
        /* Refresh flows list */
        await fetchFlows();
      } catch (error) {
        toast.error(getApiErrorMessage(error, "Failed to delete flow"));
      } finally {
        setLoading(false);
      }
    }
  };

  const closeModal = () => {
    setShowFlowModal(false);
    setCurrentFlow(getEmptyFlow());
    setCurrentVTMapping({ valve_id: "", tank_id: "", pump_id: "" });
    setEditingFlowId(null);
  };

  /* ─── BULK SUBMIT HELPERS ─── */
  const submitBulkUGTanks = async (ugTanks) => {
    if (
      !validateRows(
        ugTanks,
        [
          { key: "ug_tank_id", label: "UG Tank ID" },
          { key: "name", label: "Name" },
          { key: "capacity", label: "Capacity", type: "number" },
          { key: "height", label: "Height", type: "number" },
          { key: "sensor_type", label: "Sensor Type" },
          {
            key: "sensor_slave_id",
            label: "Sensor Slave ID",
            type: "number",
            min: 1,
            max: 500,
          },
        ],
        "UG tank",
      )
    ) {
      return;
    }

    const seenUgTankIds = new Set();
    const duplicateUgTank = ugTanks.find((tank) => {
      const normalizedId = String(tank.ug_tank_id || "").trim().toLowerCase();
      if (seenUgTankIds.has(normalizedId)) {
        return true;
      }
      seenUgTankIds.add(normalizedId);
      return false;
    });
    const duplicateUgTankId = duplicateUgTank?.ug_tank_id;
    if (duplicateUgTankId) {
      toast.error(`UG tank ID "${duplicateUgTankId}" is duplicated in this batch`);
      return;
    }

    const existingUgTankIds = new Set(
      ugTanksData.map((tank) =>
        String(tank.ug_tank_id || "").trim().toLowerCase(),
      ),
    );
    const existingUgTank = ugTanks.find((tank) =>
      existingUgTankIds.has(String(tank.ug_tank_id || "").trim().toLowerCase()),
    );
    if (existingUgTank) {
      toast.error(`UG tank ID "${existingUgTank.ug_tank_id}" already exists`);
      return;
    }

    setLoading(true);
    try {
      await api.createUGTanks(ugTanks);
      toast.success("UG tanks created successfully");
      setUgTanks([]);
      setUgCount("");
      await refetchDropdownData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Failed to create UG Tanks"));
    } finally {
      setLoading(false);
    }
  };

  const submitBulkTowers = async (towers) => {
    if (
      !validateRows(
        towers,
        [
          { key: "name", label: "Name" },
          { key: "location", label: "Location" },
        ],
        "tower",
      )
    ) {
      return;
    }

    setLoading(true);
    try {
      await api.createTowers(towers);
      toast.success("Towers created successfully");
      setTowers([]);
      setTowerCount("");
      await refetchDropdownData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Failed to create towers"));
    } finally {
      setLoading(false);
    }
  };

  const submitBulkTanks = async (tanks) => {
    if (
      !validateRows(
        tanks,
        [
          { key: "tank_id", label: "Tank ID" },
          { key: "name", label: "Name" },
          { key: "capacity", label: "Capacity", type: "number" },
          { key: "low", label: "Low", type: "number", min: 0, max: 100 },
          { key: "med", label: "Mid", type: "number", min: 0, max: 100 },
          { key: "high", label: "High", type: "number", min: 0, max: 100 },
          { key: "sensor_type", label: "Sensor Type" },
          {
            key: "sensor_slave_id",
            label: "Sensor Slave ID",
            type: "number",
            min: 1,
            max: 500,
          },
        ],
        "tank",
      )
    ) {
      return;
    }

    const seenTankIds = new Set();
    const duplicateTank = tanks.find((tank) => {
      const normalizedId = String(tank.tank_id || "").trim().toLowerCase();
      if (seenTankIds.has(normalizedId)) {
        return true;
      }
      seenTankIds.add(normalizedId);
      return false;
    });
    const duplicateTankId = duplicateTank?.tank_id;
    if (duplicateTankId) {
      toast.error(`Tank ID "${duplicateTankId}" is duplicated in this batch`);
      return;
    }

    const existingTankIds = new Set(
      tanksData.map((tank) =>
        String(tank.tank_id || "").trim().toLowerCase(),
      ),
    );
    const existingTank = tanks.find((tank) =>
      existingTankIds.has(String(tank.tank_id || "").trim().toLowerCase()),
    );
    if (existingTank) {
      toast.error(
        `Tank ID "${existingTank.tank_id}" already exists in this society`,
      );
      return;
    }

    setLoading(true);
    try {
      await api.createBulkTanks(tanks);
      toast.success("Tanks created successfully");
      setTanks([]);
      setTankCount("");
      await refetchDropdownData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Failed to create tanks"));
    } finally {
      setLoading(false);
    }
  };

  const submitBulkLevelControllers = async (levelDevices) => {
    if (
      !validateRows(
        levelDevices,
        [
          { key: "device_id", label: "Device ID" },
          { key: "name", label: "Name" },
          {
            label: "Connection Type",
            type: "number",
            min: 1,
            max: 2,
            getValue: (row) => row.network?.connectionType,
          },
          {
            label: "GSM ISP",
            type: "number",
            getValue: (row) => row.network?.gsmISP,
          },
        ],
        "level controller",
      )
    ) {
      return;
    }

    setLoading(true);
    try {
      await api.createBulkLevelControllers(levelDevices);
      toast.success("Level controllers created successfully");
      setLevelDevices([]);
      setLevelCount("");
      await refetchDropdownData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Failed to create level controllers"));
    } finally {
      setLoading(false);
    }
  };

  const submitBulkPumpControllers = async (pumpControllers) => {
    if (
      !validateRows(
        pumpControllers,
        [
          { key: "device_id", label: "Device ID" },
          { key: "name", label: "Name" },
          {
            label: "Connection Type",
            type: "number",
            min: 1,
            max: 2,
            getValue: (row) => row.network?.connectionType,
          },
          {
            label: "GSM ISP",
            type: "number",
            allowedValues: GSM_APN_OPTIONS.map((option) => option.value),
            allowedMessage: GSM_APN_OPTIONS.map((option) => option.label).join(", "),
            getValue: (row) => row.network?.gsmISP,
          },
        ],
        "pump controller",
      )
    ) {
      return;
    }

    setLoading(true);
    try {
      await api.createBulkPumpControllers(pumpControllers);
      toast.success("Pump controllers created successfully");
      setPumpControllers([]);
      setPumpControllerCount("");
      await refetchDropdownData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Failed to create pump controllers"));
    } finally {
      setLoading(false);
    }
  };

  const submitBulkPumps = async (pumps) => {
    if (
      !validateRows(
        pumps,
        [
          { key: "pump_id", label: "Pump ID" },
          { key: "name", label: "Name" },
          { key: "power_rating", label: "Power Rating", type: "number" },
          {
            label: "Energy Meter Status",
            type: "number",
            min: 0,
            max: 1,
            getValue: (row) => row.energymeter?.status,
          },
          {
            label: "Energy Meter Make",
            type: "number",
            getValue: (row) => row.energymeter?.make,
          },
          {
            label: "Energy Meter Slave ID",
            type: "number",
            min: 1,
            max: 500,
            getValue: (row) => row.energymeter?.slave_id,
          },
        ],
        "pump",
      )
    ) {
      return;
    }

    setLoading(true);
    try {
      await api.createBulkPumps(pumps);
      toast.success("Pumps created successfully");
      setPumps([]);
      setPumpCount("");
      await refetchDropdownData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Failed to create pumps"));
    } finally {
      setLoading(false);
    }
  };

  const submitBulkValves = async (valves) => {
    if (
      !validateRows(
        valves,
        [
          { key: "valve_id", label: "Valve ID" },
          { key: "type", label: "Valve Type" },
        ],
        "valve",
      )
    ) {
      return;
    }

    setLoading(true);
    try {
      await api.createBulkValves(valves);
      toast.success("Valves created successfully");
      setValves([]);
      setValveCount("");
      await refetchDropdownData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Failed to create valves"));
    } finally {
      setLoading(false);
    }
  };

  /* ─── SHARED PROPS FOR TREE COMPONENTS ─── */
  const treeProps = {
    societiesData,
    adminsData,
    ugTanksData,
    towersData,
    pumpControllersData,
    pumpsData,
    levelControllersData,
    tanksData,
    valvesData,
  };

  /* ══════════════════════════════════════════
     RENDER
  ══════════════════════════════════════════ */
  return (
    <Layout>
      {/* ─── FLOW MODAL ─── */}
      {showFlowModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl h-[calc(100vh-2rem)] max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="flex shrink-0 items-center justify-between px-6 py-4 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white">
              <div>
                <h2 className="text-2xl font-bold text-slate-800">
                  {editingFlowId ? "Edit Flow" : "Add New Flow"}
                </h2>
                <p className="text-sm text-slate-500 mt-0.5">
                  {editingFlowId
                    ? "Update your water flow configuration"
                    : "Configure your water flow path"}
                </p>
              </div>
              <button
                onClick={closeModal}
                className="text-slate-400 hover:text-slate-700 transition-colors p-2 rounded-lg hover:bg-slate-200"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto bg-slate-50/80 p-4 lg:overflow-hidden">
              <div className="grid min-h-0 grid-cols-1 gap-4 lg:h-full lg:grid-cols-[minmax(0,1fr)_360px]">
                <div className="min-h-0 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm lg:h-full">
                  {/* Tabs Navigation */}
                  <Tabs
                    defaultValue="basic"
                    className="flex h-full min-h-0 flex-col overflow-hidden"
                  >
                    <TabsList className="w-full shrink-0 justify-start overflow-x-auto border-b border-slate-200 rounded-none bg-slate-50 px-5 py-0 gap-6">
                <TabsTrigger
                  value="basic"
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-500 data-[state=active]:bg-transparent px-0 py-3"
                >
                  <Building2 className="h-4 w-4 mr-2" />
                  Basic Info
                </TabsTrigger>
                <TabsTrigger
                  value="pump"
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-yellow-500 data-[state=active]:bg-transparent px-0 py-3"
                >
                  <Zap className="h-4 w-4 mr-2" />
                  Pump Config
                </TabsTrigger>
                <TabsTrigger
                  value="level"
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-indigo-500 data-[state=active]:bg-transparent px-0 py-3"
                >
                  <Network className="h-4 w-4 mr-2" />
                  Level Control
                </TabsTrigger>
              </TabsList>

                    {/* Tab Content */}
                    <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain">
                {/* TAB 1: BASIC INFO */}
                <TabsContent value="basic" className="px-6 pt-6 pb-12 space-y-6 m-0">
                  {/* Mandatory Section */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 pb-3 border-b border-slate-200">
                      <div className="w-1 h-6 bg-blue-500 rounded-full"></div>
                      <h3 className="font-semibold text-slate-800">
                        Basic Configuration
                      </h3>
                    </div>

                    {/* Tower */}
                    <div>
                      <Label className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-2">
                        <Layers className="h-4 w-4 text-purple-500" />
                        Select Tower <span className="text-red-500">*</span>
                      </Label>
                      <Select
                        value={currentFlow.tower_id}
                        onValueChange={(v) =>
                          setCurrentFlow({ ...currentFlow, tower_id: v })
                        }
                      >
                        <SelectTrigger className="w-full h-10">
                          <SelectValue placeholder="Choose a tower" />
                        </SelectTrigger>
                        <SelectContent>
                          {availableTowers.map((t) => (
                            <SelectItem key={t.id} value={t.id}>
                              {t.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Assign Users */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <Label className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                          <Users className="h-4 w-4 text-slate-600" />
                          Assign Users <span className="text-red-500">*</span>
                        </Label>
                        {getFlowUserIds(currentFlow).length > 0 && (
                          <button
                            onClick={() =>
                              setCurrentFlow({
                                ...currentFlow,
                                user_ids: [],
                                user_id: "",
                              })
                            }
                            className="text-xs text-slate-400 hover:text-slate-600 underline font-medium"
                          >
                            Clear
                          </button>
                        )}
                      </div>
                      <div className="rounded-lg border border-slate-200 bg-white max-h-44 overflow-y-auto">
                        {adminsData.length > 0 ? (
                          adminsData.map((admin) => {
                            const selected = getFlowUserIds(currentFlow).includes(
                              admin.id,
                            );
                            return (
                              <button
                                key={admin.id}
                                type="button"
                                onClick={() => toggleAssignedUser(admin.id)}
                                className={`w-full flex items-center justify-between gap-3 px-3 py-2 text-left text-sm transition-colors ${
                                  selected
                                    ? "bg-slate-100 text-slate-900"
                                    : "hover:bg-slate-50 text-slate-700"
                                }`}
                              >
                                <span className="truncate">
                                  {admin.name || admin.email}
                                </span>
                                <span
                                  className={`h-4 w-4 rounded border flex items-center justify-center flex-shrink-0 ${
                                    selected
                                      ? "bg-slate-900 border-slate-900"
                                      : "border-slate-300"
                                  }`}
                                >
                                  {selected && (
                                    <span className="h-1.5 w-1.5 rounded-full bg-white" />
                                  )}
                                </span>
                              </button>
                            );
                          })
                        ) : (
                          <div className="px-3 py-3 text-sm text-slate-500">
                            No admin users available
                          </div>
                        )}
                      </div>
                      {getFlowUserIds(currentFlow).length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {getFlowUserIds(currentFlow).map((adminId) => (
                            <span
                              key={adminId}
                              className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700"
                            >
                              {getAdminLabel(adminId)}
                              <button
                                type="button"
                                onClick={() => toggleAssignedUser(adminId)}
                                className="text-slate-400 hover:text-red-600"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                      <p className="text-xs text-slate-500 mt-1">
                        Assign one or more admin users to manage this tower
                      </p>
                    </div>
                  </div>

                  {/* Optional UG Tank Section */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 pb-3 border-b border-slate-200">
                      <div className="w-1 h-6 bg-cyan-500 rounded-full"></div>
                      <h3 className="font-semibold text-slate-800">
                        Source Tank
                      </h3>
                      <span className="ml-auto text-xs px-2 py-1 bg-cyan-50 text-cyan-700 rounded-full border border-cyan-200">
                        Optional
                      </span>
                    </div>

                    {/* UG Tank */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <Label className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                          <Droplets className="h-4 w-4 text-cyan-500" />
                          Select UG Tank
                        </Label>
                        {currentFlow.ug_tank_id && (
                          <button
                            onClick={() =>
                              setCurrentFlow({ ...currentFlow, ug_tank_id: "" })
                            }
                            className="text-xs text-slate-400 hover:text-slate-600 underline font-medium"
                          >
                            Clear
                          </button>
                        )}
                      </div>
                      <Select
                        value={currentFlow.ug_tank_id}
                        onValueChange={(v) =>
                          setCurrentFlow({ ...currentFlow, ug_tank_id: v })
                        }
                      >
                        <SelectTrigger className="w-full h-10">
                          <SelectValue placeholder="Choose a UG tank (optional)" />
                        </SelectTrigger>
                        <SelectContent>
                          {ugTanksData.map((t) => (
                            <SelectItem key={t.ug_tank_id} value={t.ug_tank_id}>
                              {t.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-slate-500 mt-1">
                        Specify the source underground tank for this flow
                        (optional)
                      </p>
                    </div>
                  </div>
                </TabsContent>

                {/* TAB 2: PUMP CONFIG */}
                <TabsContent value="pump" className="px-6 pt-6 pb-12 space-y-6 m-0">
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 pb-3 border-b border-slate-200">
                      <div className="w-1 h-6 bg-yellow-500 rounded-full"></div>
                      <h3 className="font-semibold text-slate-800">
                        Pump Configuration
                      </h3>
                      <span className="ml-auto text-xs px-2 py-1 bg-yellow-50 text-yellow-700 rounded-full border border-yellow-200">
                        Optional
                      </span>
                    </div>

                    {pumpControllersData.length > 0 ? (
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <Label className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                            <Zap className="h-4 w-4 text-yellow-500" />
                            Select Pump Controller
                          </Label>
                          {currentFlow.pump_controller_id && (
                            <button
                              onClick={() =>
                                setCurrentFlow({
                                  ...currentFlow,
                                  pump_controller_id: "",
                                })
                              }
                              className="text-xs text-slate-400 hover:text-slate-600 underline font-medium"
                            >
                              Clear
                            </button>
                          )}
                        </div>
                        <Select
                          value={currentFlow.pump_controller_id}
                          onValueChange={(v) =>
                            setCurrentFlow({
                              ...currentFlow,
                              pump_controller_id: v,
                            })
                          }
                        >
                          <SelectTrigger className="w-full h-10">
                            <SelectValue placeholder="Choose a pump controller" />
                          </SelectTrigger>
                          <SelectContent>
                            {pumpControllersData.map((pc) => (
                              <SelectItem key={pc.device_id} value={pc.device_id}>
                                {pc.device_id}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-slate-500 mt-1">
                          Controls the pump operation and water flow. Specific
                          pumps can be selected for individual valve & tank
                          mappings in the Level Control tab.
                        </p>
                      </div>
                    ) : (
                      <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-600">
                        No pump controllers available. Create some in the
                        Devices tab first.
                      </div>
                    )}
                  </div>
                </TabsContent>

                {/* TAB 3: LEVEL CONTROL */}
                <TabsContent value="level" className="px-6 pt-6 pb-12 space-y-6 m-0">
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 pb-3 border-b border-slate-200">
                      <div className="w-1 h-6 bg-indigo-500 rounded-full"></div>
                      <h3 className="font-semibold text-slate-800">
                        Level Control
                      </h3>
                      <span className="ml-auto text-xs px-2 py-1 bg-indigo-50 text-indigo-700 rounded-full border border-indigo-200">
                        Optional
                      </span>
                    </div>

                    {levelControllersData.length > 0 ? (
                      <>
                        {/* Level Controller */}
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <Label className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                              <Network className="h-4 w-4 text-indigo-500" />
                              Select Level Controller
                            </Label>
                            {currentFlow.level_controller_id && (
                              <button
                                onClick={() =>
                                  setCurrentFlow({
                                    ...currentFlow,
                                    level_controller_id: "",
                                    valve_tank_mappings: [],
                                  })
                                }
                                className="text-xs text-slate-400 hover:text-slate-600 underline font-medium"
                              >
                                Clear
                              </button>
                            )}
                          </div>
                          <Select
                            value={currentFlow.level_controller_id}
                            onValueChange={(v) =>
                              setCurrentFlow({
                                ...currentFlow,
                                level_controller_id: v,
                                valve_tank_mappings: [],
                              })
                            }
                          >
                            <SelectTrigger className="w-full h-10">
                              <SelectValue placeholder="Choose a level controller" />
                            </SelectTrigger>
                            <SelectContent>
                              {availableLevelControllers.map((lc) => (
                                <SelectItem key={lc.device_id} value={lc.device_id}>
                                  {lc.device_id}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <p className="text-xs text-slate-500 mt-1">
                            Monitors and controls water level
                          </p>
                        </div>

                        {/* Valve + Tank + Pump Mappings - Only show if level controller is selected */}
                        {currentFlow.level_controller_id && (
                          <div className="pt-4 border-t border-slate-100">
                            <Label className="text-sm font-semibold text-slate-700 mb-2 block">
                              Valve & Tank Mappings
                            </Label>
                            <p className="text-xs text-slate-500 mb-4">
                              Add one or more valve and/or tank pairs with
                              optional pump selection for this level controller.
                            </p>

                            {/* Input Grid */}
                            <div
                              className={`${currentFlow.pump_controller_id ? "md:grid-cols-3" : "md:grid-cols-2"} grid grid-cols-1 gap-4 mb-4 p-4 bg-slate-50 rounded-lg border border-slate-200`}
                            >
                              <div>
                                <Label className="text-xs text-slate-600 font-medium mb-2 flex items-center gap-1">
                                  <FlaskConical className="h-3 w-3 text-pink-500" />
                                  Valve (Optional)
                                </Label>
                                <Select
                                  value={currentVTMapping.valve_id}
                                  onValueChange={(v) =>
                                    setCurrentVTMapping({
                                      ...currentVTMapping,
                                      valve_id: v,
                                    })
                                  }
                                >
                                  <SelectTrigger className="h-10">
                                    <SelectValue placeholder="Select valve" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {availableValves.map((v) => (
                                      <SelectItem key={v.valve_id} value={v.valve_id}>
                                        {v.valve_id}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              <div>
                                <Label className="text-xs text-slate-600 font-medium mb-2 flex items-center gap-1">
                                  <Waves className="h-3 w-3 text-teal-500" />
                                  Tank (Optional)
                                </Label>
                                <Select
                                  value={currentVTMapping.tank_id}
                                  onValueChange={(v) =>
                                    setCurrentVTMapping({
                                      ...currentVTMapping,
                                      tank_id: v,
                                    })
                                  }
                                >
                                  <SelectTrigger className="h-10">
                                    <SelectValue placeholder="Select tank" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {availableTanks.map((t) => (
                                      <SelectItem key={t.tank_id} value={t.tank_id}>
                                        {t.tank_id}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              {currentFlow.pump_controller_id && (
                                <div>
                                  <Label className="text-xs text-slate-600 font-medium mb-2 flex items-center gap-1">
                                    <Activity className="h-3 w-3 text-orange-500" />
                                    Pump (Optional)
                                  </Label>
                                  <Select
                                    value={currentVTMapping.pump_id}
                                    onValueChange={(v) =>
                                      setCurrentVTMapping({
                                        ...currentVTMapping,
                                        pump_id: v,
                                      })
                                    }
                                  >
                                    <SelectTrigger className="h-10">
                                      <SelectValue placeholder="Select pump" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {pumpsData.map((p) => (
                                        <SelectItem key={p.pump_id} value={p.pump_id}>
                                          {p.pump_id}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                              )}
                            </div>

                            {/* Add Mapping Button */}
                            <Button
                              size="sm"
                              onClick={addVTMapping}
                              disabled={
                                !currentVTMapping.valve_id &&
                                !currentVTMapping.tank_id
                              }
                              className="w-full flex items-center gap-1 bg-slate-900 hover:bg-slate-700 text-white"
                            >
                              <Plus className="h-4 w-4" /> Add Mapping
                            </Button>

                            {/* Saved Mappings List */}
                            {currentFlow.valve_tank_mappings.length > 0 && (
                              <div className="mt-4 space-y-2">
                                <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
                                  Added Mappings (
                                  {currentFlow.valve_tank_mappings.length})
                                </p>
                                <div className="space-y-2 max-h-40 overflow-y-auto">
                                  {currentFlow.valve_tank_mappings.map(
                                    (m, i) => (
                                      <div
                                        key={i}
                                        className="flex items-center justify-between bg-white rounded-lg border border-slate-200 px-3 py-2 text-sm hover:border-slate-300 transition-colors"
                                      >
                                        <div className="flex items-center gap-3 flex-wrap">
                                          {m.pump_id && (
                                            <>
                                              <span className="text-orange-600 font-medium flex items-center gap-1">
                                                <Activity className="h-3 w-3" />
                                                {pumpsData.find(
                                                  (p) => p.pump_id === m.pump_id,
                                                )?.pump_id || m.pump_id}
                                              </span>
                                              {(m.valve_id || m.tank_id) && (
                                                <ChevronRight className="h-3 w-3 text-slate-300" />
                                              )}
                                            </>
                                          )}
                                          {m.valve_id && (
                                            <>
                                              <span className="text-pink-600 font-medium flex items-center gap-1">
                                                <FlaskConical className="h-3 w-3" />
                                                {valvesData.find(
                                                  (v) => v.valve_id === m.valve_id,
                                                )?.valve_id || m.valve_id}
                                              </span>
                                              {m.tank_id && (
                                                <ChevronRight className="h-3 w-3 text-slate-300" />
                                              )}
                                            </>
                                          )}
                                          {m.tank_id && (
                                            <span className="text-teal-600 font-medium flex items-center gap-1">
                                              <Waves className="h-3 w-3" />
                                              {tanksData.find(
                                                (t) => t.tank_id === m.tank_id,
                                              )?.tank_id || m.tank_id}
                                            </span>
                                          )}
                                        </div>
                                        <button
                                          onClick={() => removeVTMapping(i)}
                                          className="text-slate-300 hover:text-red-600 transition-colors p-1 rounded hover:bg-red-50 flex-shrink-0"
                                        >
                                          <X className="h-4 w-4" />
                                        </button>
                                      </div>
                                    ),
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="p-4 bg-indigo-50 border border-indigo-200 rounded-lg text-sm text-indigo-700">
                        No level controllers available. Create some in the
                        Devices tab first.
                      </div>
                    )}
                  </div>
                </TabsContent>
                    </div>
                  </Tabs>
                </div>

                <LivePreviewTree
                  flow={currentFlow}
                  currentVTMapping={currentVTMapping}
                  {...treeProps}
                />
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex shrink-0 justify-end gap-3 px-6 py-4 border-t border-slate-100 bg-white rounded-b-2xl">
              <Button
                variant="outline"
                onClick={closeModal}
                className="min-w-20"
              >
                Cancel
              </Button>
              <Button
                onClick={saveFlow}
                className="min-w-28 bg-slate-900 hover:bg-slate-700 text-white font-medium"
              >
                {editingFlowId ? "Update Flow" : "Save Flow"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ─── PAGE ─── */}
      <div className="space-y-6">
        <div>
          <h1 className="text-4xl font-bold text-slate-900">Setup Wizard</h1>
          <p className="text-slate-600 mt-1">
            Register society infrastructure and devices
          </p>
        </div>

        <Tabs defaultValue="society">
          <TabsList className="grid grid-cols-4 w-full">
            <TabsTrigger value="society">
              <Building2 className="h-4 w-4 mr-1" />
              Society Details
            </TabsTrigger>
            <TabsTrigger value="infra">
              <Droplets className="h-4 w-4 mr-1" />
              Infrastructure
            </TabsTrigger>
            <TabsTrigger value="devices">
              <Cpu className="h-4 w-4 mr-1" />
              Devices
            </TabsTrigger>
            <TabsTrigger value="architecture">
              <GitBranch className="h-4 w-4 mr-1" />
              Architecture
            </TabsTrigger>
          </TabsList>

          {/* ═══ SOCIETY ═══ */}
          <TabsContent value="society">
            <Card>
              <CardHeader>
                <CardTitle>Society Details</CardTitle>
                <CardDescription>
                  {societiesData?.id
                    ? "View or update society information"
                    : "Register society information"}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {(!societiesData?.id || editingSociety) && (
                  <form
                    onSubmit={submitSociety}
                    className="grid md:grid-cols-2 gap-4"
                  >
                    <div>
                      <Label>Society Name *</Label>
                      <Input
                        required
                        value={society.name}
                        onChange={(e) =>
                          setSociety({ ...society, name: e.target.value })
                        }
                      />
                    </div>
                    <div>
                      <Label>Location</Label>
                      <Input
                        value={society.location}
                        onChange={(e) =>
                          setSociety({ ...society, location: e.target.value })
                        }
                      />
                    </div>
                    <div className="md:col-span-2 flex gap-2">
                      <Button disabled={loading}>
                        {editingSociety ? "Update Society" : "Submit"}
                      </Button>
                      {editingSociety && (
                        <Button
                          type="button"
                          variant="outline"
                          onClick={cancelEditSociety}
                          disabled={loading}
                        >
                          Cancel
                        </Button>
                      )}
                    </div>
                  </form>
                )}

                {societiesData?.id && !editingSociety && (
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                          Current Society
                        </p>
                        <h3 className="mt-1 text-lg font-semibold text-slate-900">
                          {societiesData.name || "-"}
                        </h3>
                        <p className="text-sm text-slate-600">
                          {societiesData.location || "No location added"}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={startEditSociety}
                          disabled={loading}
                        >
                          Edit
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={deleteSociety}
                          disabled={loading}
                          className="text-red-600 hover:bg-red-50 hover:text-red-700"
                        >
                          Delete
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ═══ INFRA ═══ */}
          <TabsContent value="infra" className="space-y-6">
            {/* UG Tanks */}
            <Card>
              <CardHeader>
                <CardTitle>Add Bulk UG Tanks</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  <Input
                    type="number"
                    max={20}
                    placeholder="Count"
                    value={ugCount}
                    onChange={(e) => setUgCount(e.target.value)}
                  />
                  <Button
                    onClick={() =>
                      setUgTanks(
                        generateRows(ugCount, {
                          ug_tank_id: "",
                          name: "",
                          capacity: "",
                          height: "",
                          sensor_type: "",
                          sensor_slave_id: "",
                        }),
                      )
                    }
                  >
                    Generate
                  </Button>
                </div>
                {ugTanks.map((tank, i) => (
                  <div key={i} className="grid md:grid-cols-6 gap-3">
                    <Input
                      required
                      placeholder="UG Tank ID"
                      value={tank.ug_tank_id}
                      onChange={(e) => {
                        const arr = [...ugTanks];
                        arr[i].ug_tank_id = e.target.value;
                        setUgTanks(arr);
                      }}
                    />
                    <Input
                      required
                      placeholder="Name"
                      value={tank.name}
                      onChange={(e) => {
                        const arr = [...ugTanks];
                        arr[i].name = e.target.value;
                        setUgTanks(arr);
                      }}
                    />
                    <Input
                      required
                      type="number"
                      placeholder="Capacity"
                      value={tank.capacity}
                      onChange={(e) => {
                        const arr = [...ugTanks];
                        arr[i].capacity = e.target.value;
                        setUgTanks(arr);
                      }}
                    />
                    <Input
                      required
                      type="number"
                      placeholder="Height"
                      value={tank.height}
                      onChange={(e) => {
                        const arr = [...ugTanks];
                        arr[i].height = e.target.value;
                        setUgTanks(arr);
                      }}
                    />
                    <Select
                      value={tank.sensor_type || ""}
                      onValueChange={(val) => {
                        const arr = [...ugTanks];
                        arr[i].sensor_type = val;
                        setUgTanks(arr);
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Sensor Type" />
                      </SelectTrigger>
                      <SelectContent>
                        {TANK_SENSOR_TYPE_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      required
                      type="number"
                      min={1}
                      max={500}
                      placeholder="Sensor Slave ID"
                      value={tank.sensor_slave_id}
                      onChange={(e) => {
                        const arr = [...ugTanks];
                        arr[i].sensor_slave_id = e.target.value;
                        setUgTanks(arr);
                      }}
                    />
                  </div>
                ))}
                {ugTanks.length > 0 && (
                  <Button onClick={() => submitBulkUGTanks(ugTanks)}>
                    Submit UG Tanks
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* Towers */}
            <Card>
              <CardHeader>
                <CardTitle>Add Bulk Towers</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  <Input
                    type="number"
                    max={20}
                    placeholder="Count"
                    value={towerCount}
                    onChange={(e) => setTowerCount(e.target.value)}
                  />
                  <Button
                    onClick={() =>
                      setTowers(
                        generateRows(towerCount, { name: "", location: "" }),
                      )
                    }
                  >
                    Generate
                  </Button>
                </div>
                {towers.map((tower, i) => (
                  <div key={i} className="grid md:grid-cols-2 gap-3">
                    <Input
                      required
                      placeholder="Tower Name"
                      value={tower.name}
                      onChange={(e) => {
                        const arr = [...towers];
                        arr[i].name = e.target.value;
                        setTowers(arr);
                      }}
                    />
                    <Input
                      required
                      placeholder="Location"
                      value={tower.location}
                      onChange={(e) => {
                        const arr = [...towers];
                        arr[i].location = e.target.value;
                        setTowers(arr);
                      }}
                    />
                  </div>
                ))}
                {towers.length > 0 && (
                  <Button onClick={() => submitBulkTowers(towers)}>
                    Submit Towers
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* Tanks */}
            <Card>
              <CardHeader>
                <CardTitle>Add Bulk Tanks</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  <Input
                    type="number"
                    max={20}
                    placeholder="Count"
                    value={tankCount}
                    onChange={(e) => setTankCount(e.target.value)}
                  />
                  <Button
                    onClick={() =>
                      setTanks(
                        generateRows(tankCount, {
                          tank_id: "",
                          name: "",
                          capacity: "",
                          low: "",
                          med: "",
                          high: "",
                          sensor_type: "",
                          sensor_slave_id: "",
                          height: "",
                          control: false,
                        }),
                      )
                    }
                  >
                    Generate
                  </Button>
                </div>
                {tanks.map((tank, i) => (
                  <div key={i} className="grid md:grid-cols-6 gap-3">
                    <Input
                      required
                      placeholder="Tank Id"
                      value={tank.tank_id}
                      onChange={(e) => {
                        const arr = [...tanks];
                        arr[i].tank_id = e.target.value;
                        setTanks(arr);
                      }}
                    />
                    <Input
                      required
                      placeholder="Name"
                      value={tank.name}
                      onChange={(e) => {
                        const arr = [...tanks];
                        arr[i].name = e.target.value;
                        setTanks(arr);
                      }}
                    />
                    <Input
                      required
                      type="number"
                      placeholder="Capacity"
                      value={tank.capacity}
                      onChange={(e) => {
                        const arr = [...tanks];
                        arr[i].capacity = e.target.value;
                        setTanks(arr);
                      }}
                    />
                    <Input
                      required
                      type="number"
                      min={0}
                      max={100}
                      placeholder="Low"
                      value={tank.low}
                      onChange={(e) => {
                        const arr = [...tanks];
                        arr[i].low = e.target.value;
                        setTanks(arr);
                      }}
                    />
                    <Input
                      required
                      type="number"
                      min={0}
                      max={100}
                      placeholder="Mid"
                      value={tank.med}
                      onChange={(e) => {
                        const arr = [...tanks];
                        arr[i].med = e.target.value;
                        setTanks(arr);
                      }}
                    />
                    <Input
                      required
                      type="number"
                      min={0}
                      max={100}
                      placeholder="High"
                      value={tank.high}
                      onChange={(e) => {
                        const arr = [...tanks];
                        arr[i].high = e.target.value;
                        setTanks(arr);
                      }}
                    />
                    <Select
                      value={tank.sensor_type || ""}
                      onValueChange={(val) => {
                        const arr = [...tanks];
                        arr[i].sensor_type = val;
                        setTanks(arr);
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Sensor Type" />
                      </SelectTrigger>
                      <SelectContent>
                        {TANK_SENSOR_TYPE_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      type="number"
                      min={1}
                      max={500}
                      placeholder="Sensor Slave ID"
                      value={tank.sensor_slave_id}
                      onChange={(e) => {
                        const arr = [...tanks];
                        arr[i].sensor_slave_id = e.target.value;
                        setTanks(arr);
                      }}
                    />
                    <Input
                      placeholder="Height"
                      value={tank.height}
                      onChange={(e) => {
                        const arr = [...tanks];
                        arr[i].height = e.target.value;
                        setTanks(arr);
                      }}
                    />
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={tank.control}
                        onCheckedChange={(val) => {
                          const arr = [...tanks];
                          arr[i].control = val;
                          setTanks(arr);
                        }}
                      />
                      <span className="text-sm">Control</span>
                    </div>
                  </div>
                ))}
                {tanks.length > 0 && (
                  <Button onClick={() => submitBulkTanks(tanks)}>
                    Submit Tanks
                  </Button>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ═══ DEVICES ═══ */}
          <TabsContent value="devices" className="space-y-6">
            {/* Level Controllers */}
            <Card>
              <CardHeader>
                <CardTitle>Add Bulk Level Controllers</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  <Input
                    type="number"
                    max={20}
                    placeholder="Count"
                    value={levelCount}
                    onChange={(e) => setLevelCount(e.target.value)}
                  />
                  <Button
                    onClick={() =>
                      setLevelDevices(
                        generateRows(levelCount, {
                          device_id: "",
                          name: "",
                          network: {
                            connectionType: "",
                            gsmISP: "",
                            wifiUsn: "",
                            wifiPwd: "",
                          },
                        }),
                      )
                    }
                  >
                    Generate
                  </Button>
                </div>
                {levelDevices.map((d, i) => (
                  <div key={i} className="grid md:grid-cols-6 gap-3">
                    <Input
                      required
                      placeholder="Device ID"
                      value={d.device_id}
                      onChange={(e) => {
                        const arr = [...levelDevices];
                        arr[i].device_id = e.target.value;
                        setLevelDevices(arr);
                      }}
                    />
                    <Input
                      required
                      placeholder="Name"
                      value={d.name}
                      onChange={(e) => {
                        const arr = [...levelDevices];
                        arr[i].name = e.target.value;
                        setLevelDevices(arr);
                      }}
                    />
                    <Select
                      value={String(
                        d.network?.connectionType || "",
                      )}
                      onValueChange={(val) => {
                        const arr = [...levelDevices];
                        arr[i].network = {
                          ...(arr[i].network || {}),
                          connectionType: Number(val),
                        };
                        setLevelDevices(arr);
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Connection Type" />
                      </SelectTrigger>
                      <SelectContent>
                        {LEVEL_CONNECTION_TYPE_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      required
                      type="number"
                      placeholder="GSM ISP"
                      value={d.network?.gsmISP ?? ""}
                      onChange={(e) => {
                        const arr = [...levelDevices];
                        arr[i].network = {
                          ...(arr[i].network || {}),
                          gsmISP: e.target.value,
                        };
                        setLevelDevices(arr);
                      }}
                    />
                    <Input
                      placeholder="WiFi Username"
                      value={d.network?.wifiUsn || ""}
                      onChange={(e) => {
                        const arr = [...levelDevices];
                        arr[i].network = {
                          ...(arr[i].network || {}),
                          wifiUsn: e.target.value,
                        };
                        setLevelDevices(arr);
                      }}
                    />
                    <Input
                      placeholder="WiFi Password"
                      value={d.network?.wifiPwd || ""}
                      onChange={(e) => {
                        const arr = [...levelDevices];
                        arr[i].network = {
                          ...(arr[i].network || {}),
                          wifiPwd: e.target.value,
                        };
                        setLevelDevices(arr);
                      }}
                    />
                  </div>
                ))}
                {levelDevices.length > 0 && (
                  <Button
                    onClick={() => submitBulkLevelControllers(levelDevices)}
                  >
                    Submit
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* Pump Controllers */}
            <Card>
              <CardHeader>
                <CardTitle>Add Bulk Pump Controllers</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  <Input
                    type="number"
                    placeholder="Count"
                    value={pumpControllerCount}
                    onChange={(e) => setPumpControllerCount(e.target.value)}
                  />
                  <Button
                    onClick={() =>
                      setPumpControllers(
                        generateRows(pumpControllerCount, {
                          device_id: "",
                          name: "",
                          network: {
                            connectionType: "",
                            gsmISP: "",
                            wifiUsn: "",
                            wifiPwd: "",
                          },
                        }),
                      )
                    }
                  >
                    Generate
                  </Button>
                </div>
                {pumpControllers.map((p, i) => (
                  <div key={i} className="grid md:grid-cols-6 gap-3">
                    <Input
                      required
                      placeholder="Device ID"
                      value={p.device_id}
                      onChange={(e) => {
                        const arr = [...pumpControllers];
                        arr[i].device_id = e.target.value;
                        setPumpControllers(arr);
                      }}
                    />
                    <Input
                      required
                      placeholder="Name"
                      value={p.name}
                      onChange={(e) => {
                        const arr = [...pumpControllers];
                        arr[i].name = e.target.value;
                        setPumpControllers(arr);
                      }}
                    />
                    <Select
                      value={String(
                        p.network?.connectionType || "",
                      )}
                      onValueChange={(val) => {
                        const arr = [...pumpControllers];
                        arr[i].network = {
                          ...(arr[i].network || {}),
                          connectionType: Number(val),
                        };
                        setPumpControllers(arr);
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Connection Type" />
                      </SelectTrigger>
                      <SelectContent>
                        {LEVEL_CONNECTION_TYPE_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select
                      value={p.network?.gsmISP ? String(p.network.gsmISP) : ""}
                      onValueChange={(val) => {
                        const arr = [...pumpControllers];
                        arr[i].network = {
                          ...(arr[i].network || {}),
                          gsmISP: Number(val),
                        };
                        setPumpControllers(arr);
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="GSM ISP" />
                      </SelectTrigger>
                      <SelectContent>
                        {GSM_APN_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      placeholder="WiFi Username"
                      value={p.network?.wifiUsn || ""}
                      onChange={(e) => {
                        const arr = [...pumpControllers];
                        arr[i].network = {
                          ...(arr[i].network || {}),
                          wifiUsn: e.target.value,
                        };
                        setPumpControllers(arr);
                      }}
                    />
                    <Input
                      placeholder="WiFi Password"
                      value={p.network?.wifiPwd || ""}
                      onChange={(e) => {
                        const arr = [...pumpControllers];
                        arr[i].network = {
                          ...(arr[i].network || {}),
                          wifiPwd: e.target.value,
                        };
                        setPumpControllers(arr);
                      }}
                    />
                  </div>
                ))}
                {pumpControllers.length > 0 && (
                  <Button
                    onClick={() => submitBulkPumpControllers(pumpControllers)}
                  >
                    Submit
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* Pumps */}
            <Card>
              <CardHeader>
                <CardTitle>Add Bulk Pumps</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  <Input
                    type="number"
                    placeholder="Count"
                    value={pumpCount}
                    onChange={(e) => setPumpCount(e.target.value)}
                  />
                  <Button
                    onClick={() =>
                      setPumps(
                        generateRows(pumpCount, {
                          pump_id: "",
                          name: "",
                          power_rating: "",
                          model: "",
                          energymeter: {
                            status: "",
                            make: "",
                            slave_id: "",
                          },
                          control: false,
                        }),
                      )
                    }
                  >
                    Generate
                  </Button>
                </div>
                {pumps.map((pump, i) => (
                  <div key={i} className="grid md:grid-cols-8 gap-3">
                    <Input
                      required
                      placeholder="Pump ID"
                      value={pump.pump_id}
                      onChange={(e) => {
                        const arr = [...pumps];
                        arr[i].pump_id = e.target.value;
                        setPumps(arr);
                      }}
                    />
                    <Input
                      required
                      placeholder="Name"
                      value={pump.name}
                      onChange={(e) => {
                        const arr = [...pumps];
                        arr[i].name = e.target.value;
                        setPumps(arr);
                      }}
                    />
                    <Input
                      required
                      type="number"
                      placeholder="Power Rating"
                      value={pump.power_rating}
                      onChange={(e) => {
                        const arr = [...pumps];
                        arr[i].power_rating = e.target.value;
                        setPumps(arr);
                      }}
                    />
                    <Input
                      placeholder="Model"
                      value={pump.model}
                      onChange={(e) => {
                        const arr = [...pumps];
                        arr[i].model = e.target.value;
                        setPumps(arr);
                      }}
                    />
                    <Select
                      value={String(pump.energymeter?.status ?? "")}
                      onValueChange={(value) => {
                        const arr = [...pumps];
                        arr[i].energymeter = {
                          ...(arr[i].energymeter || {}),
                          status: value,
                        };
                        setPumps(arr);
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="EM Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">Connected</SelectItem>
                        <SelectItem value="0">Not connected</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input
                      required
                      type="number"
                      placeholder="EM Make"
                      value={pump.energymeter?.make ?? ""}
                      onChange={(e) => {
                        const arr = [...pumps];
                        arr[i].energymeter = {
                          ...(arr[i].energymeter || {}),
                          make: e.target.value,
                        };
                        setPumps(arr);
                      }}
                    />
                    <Input
                      required
                      type="number"
                      min={1}
                      max={500}
                      placeholder="EM Slave ID"
                      value={pump.energymeter?.slave_id ?? ""}
                      onChange={(e) => {
                        const arr = [...pumps];
                        arr[i].energymeter = {
                          ...(arr[i].energymeter || {}),
                          slave_id: e.target.value,
                        };
                        setPumps(arr);
                      }}
                    />
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={pump.control}
                        onCheckedChange={(val) => {
                          const arr = [...pumps];
                          arr[i].control = val;
                          setPumps(arr);
                        }}
                      />
                      <span className="text-sm">Control</span>
                    </div>
                  </div>
                ))}
                {pumps.length > 0 && (
                  <Button onClick={() => submitBulkPumps(pumps)}>
                    Submit Pumps
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* Valves */}
            <Card>
              <CardHeader>
                <CardTitle>Add Bulk Valves</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  <Input
                    type="number"
                    placeholder="Count"
                    value={valveCount}
                    onChange={(e) => setValveCount(e.target.value)}
                  />
                  <Button
                    onClick={() =>
                      setValves(
                        generateRows(valveCount, {
                          valve_id: "",
                          type: "motorized",
                          control: false,
                        }),
                      )
                    }
                  >
                    Generate
                  </Button>
                </div>
                {valves.map((v, i) => (
                  <div key={i} className="grid md:grid-cols-3 gap-3">
                    <Input
                      required
                      placeholder="Valve ID"
                      value={v.valve_id}
                      onChange={(e) => {
                        const arr = [...valves];
                        arr[i].valve_id = e.target.value;
                        setValves(arr);
                      }}
                    />
                    <Select
                      value={v.type}
                      onValueChange={(val) => {
                        const arr = [...valves];
                        arr[i].type = val;
                        setValves(arr);
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="motorized">Motorized</SelectItem>
                        <SelectItem value="manual">Manual</SelectItem>
                      </SelectContent>
                    </Select>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={v.control}
                        onCheckedChange={(val) => {
                          const arr = [...valves];
                          arr[i].control = val;
                          setValves(arr);
                        }}
                      />
                      <span className="text-sm">Control</span>
                    </div>
                  </div>
                ))}
                {valves.length > 0 && (
                  <Button onClick={() => submitBulkValves(valves)}>
                    Submit Valves
                  </Button>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ═══ ARCHITECTURE ═══ */}
          <TabsContent value="architecture">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Architecture Configuration</CardTitle>
                    <CardDescription className="mt-1">
                      {savedFlows.length === 0
                        ? 'No flows configured yet. Click "Add Flow" to map your first water flow path.'
                        : `${savedFlows.length} flow${savedFlows.length > 1 ? "s" : ""} configured`}
                    </CardDescription>
                  </div>
                  <Button
                    onClick={openNewFlow}
                    className="flex items-center gap-2 bg-slate-900 hover:bg-slate-700 text-white"
                  >
                    <Plus className="h-4 w-4" />
                    Add Flow
                  </Button>
                </div>
              </CardHeader>

              <CardContent>
                {savedFlows.length === 0 ? (
                  /* Empty state */
                  <div className="flex flex-col items-center justify-center py-20 text-center">
                    <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mb-4">
                      <GitBranch className="h-8 w-8 text-slate-400" />
                    </div>
                    <p className="text-slate-500 text-sm max-w-xs">
                      Map your society's complete water distribution path — from
                      underground tanks all the way to individual overhead
                      tanks.
                    </p>
                    <Button
                      onClick={openNewFlow}
                      variant="outline"
                      className="mt-6 flex items-center gap-2"
                    >
                      <Plus className="h-4 w-4" /> Add Your First Flow
                    </Button>
                  </div>
                ) : (
                  /* Saved flows */
                  <div className="space-y-4">
                    {savedFlows.map((flow, index) => (
                      <div
                        key={flow.id}
                        className="rounded-xl border border-slate-200 bg-slate-50/50 overflow-hidden"
                      >
                        {/* Flow Container */}
                        <div className="p-5 relative">
                          {/* Flow index badge */}
                          <div className="absolute top-4 left-4 w-6 h-6 rounded-full bg-slate-800 text-white text-xs font-bold flex items-center justify-center">
                            {index + 1}
                          </div>

                          <div className="pl-8">
                            <FlowTree flow={flow} {...treeProps} />
                          </div>
                        </div>

                        {/* Actions Section */}
                        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-200 bg-slate-100/50">
                          <button
                            onClick={() => openEditFlow(flow)}
                            className="p-2 rounded-lg hover:bg-blue-100 text-blue-600 hover:text-blue-700 transition-all"
                            title="Edit flow"
                          >
                            <Edit2 className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => deleteFlow(flow.id)}
                            className="p-2 rounded-lg hover:bg-red-100 text-red-600 hover:text-red-700 transition-all"
                            title="Delete flow"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default DeviceRegistration;
